import React, { useEffect } from 'react';
import confetti from 'canvas-confetti';

interface ResultsViewProps {
  correctCount?: number;
  incorrectCount?: number;
  results?: { correct: number; incorrect: number };
  practice?: any;
  onFinish?: () => void;
  onPracticeAgain?: () => void;
  compact?: boolean; // Optional prop for a more compact display
  className?: string; // Optional class name for styling
}

/**
 * A reusable component to display practice results
 */
const ResultsView: React.FC<ResultsViewProps> = ({ 
  correctCount: propCorrectCount, 
  incorrectCount: propIncorrectCount,
  results,
  practice,
  onFinish,
  onPracticeAgain,
  compact = false,
  className = ''
}) => {
  // Use either direct props or extract from results object
  const correctCount = propCorrectCount !== undefined ? propCorrectCount : (results?.correct || 0);
  const incorrectCount = propIncorrectCount !== undefined ? propIncorrectCount : (results?.incorrect || 0);
  
  const totalWords = correctCount + incorrectCount;
  
  // Celebrate with confetti when results are shown
  useEffect(() => {
    // Only show celebration confetti if there are correct answers
    if (correctCount > 0) {
      // First burst
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
      
      // Second burst after a short delay
      const timer = setTimeout(() => {
        confetti({
          particleCount: 50,
          angle: 60,
          spread: 55,
          origin: { x: 0, y: 0.6 }
        });
        
        confetti({
          particleCount: 50,
          angle: 120,
          spread: 55,
          origin: { x: 1, y: 0.6 }
        });
      }, 700);
      
      return () => clearTimeout(timer);
    }
  }, [correctCount]);

  // Calculate a fun message based on performance
  const getMessage = () => {
    if (totalWords === 0) return { text: "Let's practice some words!", emoji: "📚" };
    
    const percentCorrect = (correctCount / totalWords) * 100;
    
    if (percentCorrect === 100) return { text: "Perfect score! Amazing job!", emoji: "🏆" };
    if (percentCorrect >= 80) return { text: "Fantastic work! Super spelling!", emoji: "🌟" };
    if (percentCorrect >= 60) return { text: "Great effort! Keep practicing!", emoji: "⭐" };
    if (percentCorrect >= 40) return { text: "Good try! You're improving!", emoji: "👍" };
    return { text: "Keep practicing! You'll get better!", emoji: "💪" };
  };
  
  const message = getMessage();

  return (
    <div className={`${className}`} data-component-name="ResultsView">
      {/* Header removed to avoid duplication - now rendered in App.tsx */}
      
      {/* Encouraging message */}
      {!compact && (
        <div className="mb-6 bg-yellow-100 border-2 border-yellow-300 p-4 rounded-xl text-center shadow-md">
          <div className="flex items-center justify-center">
            <span className="text-4xl mr-3">{message.emoji}</span>
            <p className="text-xl font-bold text-yellow-800">{message.text}</p>
          </div>
        </div>
      )}

      <div className={`grid grid-cols-3 gap-4 ${compact ? 'mb-4' : 'mb-8'}`}>
        <div className={`bg-gradient-to-b from-indigo-100 to-indigo-200 ${compact ? 'p-3' : 'p-5'} rounded-xl text-center shadow-md border-2 border-indigo-300 transform hover:scale-105 transition-transform`}>
          <div className="flex flex-col items-center">
            <span className="text-2xl mb-1">📚</span>
            <p className={`${compact ? 'text-xs' : 'text-sm'} text-indigo-700 font-bold`}>Total Words</p>
            <p className={`${compact ? 'text-xl' : 'text-3xl'} font-bold text-indigo-800`}>{totalWords}</p>
          </div>
        </div>

        <div className={`bg-gradient-to-b from-green-100 to-green-200 ${compact ? 'p-3' : 'p-5'} rounded-xl text-center shadow-md border-2 border-green-300 transform hover:scale-105 transition-transform`}>
          <div className="flex flex-col items-center">
            <span className="text-2xl mb-1">✅</span>
            <p className={`${compact ? 'text-xs' : 'text-sm'} text-green-700 font-bold`}>Correct</p>
            <p className={`${compact ? 'text-xl' : 'text-3xl'} font-bold text-green-800`}>{correctCount}</p>
          </div>
        </div>

        <div className={`bg-gradient-to-b from-orange-100 to-orange-200 ${compact ? 'p-3' : 'p-5'} rounded-xl text-center shadow-md border-2 border-orange-300 transform hover:scale-105 transition-transform`}>
          <div className="flex flex-col items-center">
            <span className="text-2xl mb-1">🔍</span>
            <p className={`${compact ? 'text-xs' : 'text-sm'} text-orange-700 font-bold`}>To Practice</p>
            <p className={`${compact ? 'text-xl' : 'text-3xl'} font-bold text-orange-800`}>{incorrectCount}</p>
          </div>
        </div>
      </div>
      
      {!compact && correctCount > 0 && (
        <div className="text-center">
          <div className="inline-block animate-bounce">
            <span className="text-3xl">👇</span>
          </div>
          <p className="font-bold text-indigo-800">Try another list!</p>
        </div>
      )}
      
      {/* Action buttons */}
      {!compact && (
        <div className="flex flex-wrap gap-3 justify-center mt-8">
          {onPracticeAgain && (
            <button 
              onClick={onPracticeAgain}
              className="px-6 py-3 bg-gradient-to-r from-green-400 to-emerald-500 text-white text-lg font-bold rounded-full hover:from-green-500 hover:to-emerald-600 transition-colors shadow-md hover:shadow-lg transform hover:scale-105 transition-transform flex items-center"
            >
              <span className="text-xl mr-2">🔄</span>
              Practice Again
            </button>
          )}
          
          {onFinish && (
            <button 
              onClick={onFinish}
              className="px-6 py-3 bg-gradient-to-r from-blue-400 to-indigo-500 text-white text-lg font-bold rounded-full hover:from-blue-500 hover:to-indigo-600 transition-colors shadow-md hover:shadow-lg transform hover:scale-105 transition-transform flex items-center"
            >
              <span className="text-xl mr-2">⬅️</span>
              Back to Lists
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ResultsView;
